// src/data/financeHub.js
//
// The Financial Wellbeing hub, described as data. One hub component renders
// one of three age tiers — the member's `ageTier` (kid / teen / adult, stamped
// at join) picks which — so the same CMS library serves a nine-year-old and a
// fifty-year-old without either seeing the other's content.
//
// Library items live in Firestore `finance_resources` (global, CMS-managed)
// plus each org's `private_libraries/finance_resources` overlay, exactly like
// Brain Health. Each item carries `ageTiers: ['kid'|'teen'|'adult']` and
// `tags`; a section below is just a tag with a title and a hook. Authoring a
// resource under a new tag makes it appear in the "More" shelf, so nothing
// published ever silently vanishes because a section wasn't defined here.

import {
    Wallet, PiggyBank, CreditCard, Sunrise, Home, HeartPulse,
    Coins, ShoppingBag, Landmark, Sparkles, Gift, Hammer
} from 'lucide-react';
import { resolveAgeTier } from '../utils/ageTier';

export const FINANCE_TAB_ID = 'finance';
export const FINANCE_LIBRARY_ID = 'finance_resources';
export const AGE_TIERS = ['kid', 'teen', 'adult'];
export const AGE_TIER_LABELS = { kid: 'Kids (under 13)', teen: 'Teens (13–17)', adult: 'Adults' };

// Per-tier hero styling: the adult hub wears the partner's colours (Scotia red,
// from contentPartners.js) so the section reads as theirs; heroClass is the
// fallback when no partner palette is configured. The younger tiers keep their
// own warmth so the page reads as "for me" at a glance, with the partner colour
// carried in the icon tiles and lockup rather than the whole hero.
export const FINANCE_TIERS = {
    adult: {
        title: 'Financial Wellbeing',
        navLabel: 'Financial Wellbeing',
        intro: 'Money stress is health stress. Clear, practical guidance for the decisions that actually keep people up at night.',
        partnerHero: true,
        heroClass: 'from-brand-primary to-brand-dark',
        sections: [
            { tag: 'Budgeting',    icon: Wallet,     title: 'Budgeting & cash flow',    hook: 'Know where it goes before it goes.' },
            { tag: 'Saving',       icon: PiggyBank,  title: 'Saving & investing',       hook: 'Emergency funds, first investments, and the boring habits that compound.' },
            { tag: 'Debt',         icon: CreditCard, title: 'Debt & credit',            hook: 'Getting ahead of it, and what your credit score actually measures.' },
            { tag: 'Retirement',   icon: Sunrise,    title: 'Retirement & the long game', hook: 'RRSPs, TFSAs, pensions — and how much is enough.' },
            { tag: 'Family',       icon: Home,       title: 'Family & big decisions',   hook: 'Buying a home, raising kids, caring for parents.' },
            { tag: 'Money Stress', icon: HeartPulse, title: 'Money & mental health',    hook: 'When finances are the thing weighing on you — and where to turn.' }
        ]
    },
    teen: {
        title: 'Money, sorted',
        navLabel: 'Money',
        intro: 'Your first paycheque, your first card, your first "wait, where did it all go?" — the stuff nobody teaches you in class.',
        heroClass: 'from-violet-600 to-indigo-700',
        sections: [
            { tag: 'First Paycheque', icon: Coins,       title: 'Your first paycheque', hook: 'Gross vs net, and why the number is smaller than you expected.' },
            { tag: 'Saving',          icon: PiggyBank,   title: 'Saving up',             hook: 'For the thing you actually want, without it taking forever.' },
            { tag: 'Spending',        icon: ShoppingBag, title: 'Spending smart',        hook: 'Subscriptions, impulse buys, and the apps designed to get your money.' },
            { tag: 'Credit Basics',   icon: Landmark,    title: 'Banks & credit basics', hook: 'Accounts, cards, and how not to start adult life in a hole.' }
        ]
    },
    kid: {
        title: 'Money Smarts',
        navLabel: 'Money Smarts',
        intro: 'Where money comes from, how to save it up, and how to spend it on things that are actually worth it.',
        heroClass: 'from-amber-400 to-orange-500',
        sections: [
            { tag: 'Needs vs Wants', icon: Sparkles, title: 'Needs vs wants', hook: 'Is it a need, or is it a really-really-want?' },
            { tag: 'Saving Jar',     icon: Gift,     title: 'The saving jar',  hook: 'Little bits add up to big things.' },
            { tag: 'Earning',        icon: Hammer,   title: 'Earning it',      hook: 'Chores, lemonade, and other ways to make your own money.' }
        ]
    }
};

// Which tier a member sees. Unknown age resolves to adult — the join flow
// gates minors explicitly, so an unstamped user is an adult on a legacy doc.
export const resolveFinanceTier = (user) => resolveAgeTier(user) || 'adult';

export const isFinanceHubEnabled = (orgData) => orgData?.features?.enableFinanceHub === true;

// An item with no ageTiers is treated as adult-only. The failure mode we care
// about is a kid seeing adult content, not an adult missing an untagged item.
export const tiersOf = (item) =>
    Array.isArray(item?.ageTiers) && item.ageTiers.length > 0 ? item.ageTiers : ['adult'];

export const resourcesForTier = (resources, tier) =>
    (resources || []).filter((r) => tiersOf(r).includes(tier));

const norm = (s) => String(s || '').trim().toLowerCase();

/**
 * Group a tier's resources into its sections. Returns only sections that have
 * something in them, plus a trailing `more` bucket for resources whose tags
 * match no section — a newly authored tag must never hide a resource.
 */
export function sectionsForTier(tier, resources) {
    const cfg = FINANCE_TIERS[tier] || FINANCE_TIERS.adult;
    const pool = resourcesForTier(resources, tier);
    const claimed = new Set();

    const sections = cfg.sections
        .map((section) => {
            const items = pool.filter((r) =>
                Array.isArray(r.tags) && r.tags.some((t) => norm(t) === norm(section.tag))
            );
            items.forEach((r) => claimed.add(r.id));
            return { ...section, items };
        })
        .filter((s) => s.items.length > 0);

    const more = pool.filter((r) => !claimed.has(r.id));
    return { config: cfg, sections, more, total: pool.length };
}
